import {
  ElementKind,
  Baseline,
  IElementID,
  IProductElement,
  IProductReference,
  OneOfElements,
  Project,
} from '../models/project.model';
import {
  BOVERKET_ID_PREFIX,
  CUSTOM_ID_PREFIX,
  NODON_ID_PREFIX,
  OKOBAUDAT_ID_PREFIX,
  IProduct,
  ProductCategories,
  ProductID,
  ProductJSON,
  ProductRecord,
  ProductSources,
  ProductSourcesUnion,
  CO2DATA_FI_ID_PREFIX,
} from '../models/product.model';
import {
  getAllProductElements,
  getAllBuildingVersions,
  getBuilding,
  getChildElements,
  isProductElement,
  ElementPath,
  isBuildingVersionElement,
} from './recursive-element.helpers';
import {
  ArrayOrRecord,
  ItemOrItemId,
  OptionallyRequired,
  PartialRecord,
  SemiPartial,
} from '../models/type-helpers.model';
import {
  updateBuildingVersionProperties,
  updateElements,
} from './project.helpers';
import { cloneDeep, isArray, isObject, mapValues, size, uniq } from 'lodash';
import {
  getId,
  hasDefinedProperties,
  omit,
  omitUndefined,
} from './object.helpers';
import { ConversionFactors } from '../models/unit.model';
import { DateTime } from 'luxon';
import { Recipe } from '../models/recipe.model';
import {
  getProductIdsInRecipes,
  getRecipesUsedInElement,
  removeDeletedProductRefsInRecipe,
} from './recipe.helpers';
import {
  ProjectChange,
  ProjectChangeType,
  type ProjectChangeMap,
} from './pipeline/update-engine.models';
import shallowEqual, {
  isDefined,
  isOneOf,
  keyArrayToRecord,
} from './array.helpers';
import { required } from './function.helpers';
import { getTimestamp } from './date.helpers';
import { mergeConversionFactors } from './conversion-factors.helpers';
import { getMean } from './math.helpers';
import { genericProductsLookup } from '../generic-products';
import {
  getEPDProductId,
  getGenericProductId,
} from './product-element.helpers';

/**
 * Type that can be used to get a product record from
 */
export type ProductRecordOwners = Baseline | ElementPath | ProductRecord;

// Don't use createProject here since it will cause circular dependencies
const emptyProduct: Readonly<IProduct> = Object.freeze({
  id: '',
  name: 'No product found',
  unit: 'kg',
  conversion_factors: { kg: 0, 'm³': 0 },
  external_identifiers: {},
  category_property_value_record: {},
  source: ProductSources.Boverket,
  categories: {},
  created_at: getTimestamp(),
  updated_at: getTimestamp(),
  characteristics: {},
});

/**
 * Get the name of a product
 * @param productId
 * @param productLookup Typically version.products
 * @param fallback If no name exist, use this
 * @returns The name of the product
 */
export const getProductName = (
  productId: string | undefined,
  productLookup: ProductRecord = genericProductsLookup,
  fallback = '-',
): string => {
  if (!productId) {
    return fallback;
  }
  return productLookup[productId]?.name ?? fallback;
};

/** Thrown when enrich cannot resolve a product id referenced by elements/recipes. */
export const MISSING_PRODUCT_ERROR = 'Could not find missing product to add';

/** Thrown / logged when a required product lookup fails. */
export const PRODUCT_NOT_FOUND_ERROR = 'Product not found';

/**
 * Get a product by id.
 * @param versionOrPath Version or path containing a version to search in
 * @param id If providing an ID we will crash if it's not found
 * @param additionalProducts Additional products to search in
 * @returns
 */
export const getProductById = <
  I extends ProductID | undefined,
  P = I extends undefined ? IProduct | undefined : IProduct,
>(
  productRecordOwner: ProductRecordOwners,
  id: I,
  additionalProducts?: ProductRecord,
): P => {
  if (id === undefined) {
    return undefined as P;
  }

  const productRecord = getProductRecord(productRecordOwner);

  // Find product in this order
  return required(
    productRecord[id] || additionalProducts?.[id] || genericProductsLookup[id],
    PRODUCT_NOT_FOUND_ERROR,
  );
};

/**
 * Get a product record from a version or path. If not found in version or path, return genericProductsLookup.
 * Mostly made for validation & testing
 * @param versionPathOrRecord
 * @returns
 */
export const getProductRecord = (
  productRecordOwner: ProductRecordOwners = genericProductsLookup,
): ProductRecord => {
  if (isBuildingVersionElement(productRecordOwner)) {
    return productRecordOwner.products;
  }
  if (isArray(productRecordOwner)) {
    const first = productRecordOwner[0];
    return isBuildingVersionElement(first)
      ? first.products
      : genericProductsLookup;
  }
  // If productRecordOwner is an empty object, return genericProductsLookup
  return size(productRecordOwner) ? productRecordOwner : genericProductsLookup;
};

/**
 * Get generic product from a product record
 * @param productRecordOwner
 * @param productElementOrId Element referencing the product
 * @returns
 */
export const getGenericProduct = (
  productRecordOwner: ProductRecordOwners | undefined,
  productElementOrId: IProductReference | ProductID,
): IProduct => {
  const productRecord = getProductRecord(productRecordOwner);
  const id = getGenericProductId(productElementOrId);
  return required(
    productRecord[id] ?? genericProductsLookup[id],
    'Generic product not found',
  );
};

/**
 * Get EPD product from a product record
 * @param productRecordOwner
 * @param productElementOrId Element referencing the product
 * @returns
 */
export const getEpdProduct = (
  productRecordOwner: ProductRecordOwners | undefined,
  productElementOrId: IProductReference | ProductID,
): IProduct | undefined => {
  const productRecord = getProductRecord(productRecordOwner);
  const id = getEPDProductId(productElementOrId);
  return id ? productRecord[id] : undefined;
};

/**
 * @deprecated: Find a product based on an ID. Use getProductById instead.
 * @param productsLookup
 * @param version
 * @param id
 * @param allowUndefined If false it will return an empty product if the product is not found
 * @returns
 */
export const getProductByIdDeprecated = (
  productsLookup: ProductRecord | undefined,
  version: Baseline | undefined,
  id: ProductID,
  reportError = false,
): IProduct => {
  // Always use the product from the version if it exists
  const product = version?.products[id] || productsLookup?.[id];

  // TODO: Sometimes a product doesn't exist, why?
  if (!product) {
    if (reportError) {
      console.error('Product not found', id);
    }
    return cloneDeep(emptyProduct);
  }

  return product;
};

/**
 * Return a IProductElement.product_id or Product.id or the provided id if it's a string
 * @param elementOrId
 * @returns
 */
export const getProductId = (
  elementOrId:
    | Pick<IProductElement, 'product_id'>
    | IProduct
    | IProductElement['product_id'],
): ProductID => {
  if (typeof elementOrId === 'string') {
    return elementOrId;
  }
  if ('product_id' in elementOrId) {
    return elementOrId.product_id;
  }
  return required(elementOrId?.id, 'missing product id');
};

/**
 * Use this in cases where product must exist
 * @param products - Record or list to read from (e.g. version.products); does not merge other catalogs
 * @param elementOrId
 * @param require - If true, throws when missing; if false, returns undefined (caller may try another record)
 */
export const getProduct = <T extends boolean = true>(
  products: ArrayOrRecord<IProduct> | undefined,
  elementOrId: OptionallyRequired<
    Pick<IProductElement, 'product_id'> | IProductElement['product_id'],
    T
  >,
  require: T = true as T,
) => {
  if (!elementOrId) {
    return required(undefined, require);
  }
  const id = getProductId(elementOrId);
  const product = Array.isArray(products)
    ? products?.find((p) => p.id === id)
    : (products as PartialRecord<string, IProduct>)?.[id];

  return required(
    product,
    require === true ? PRODUCT_NOT_FOUND_ERROR : require,
  );
};

/**
 * Get a record of all used products from an element
 * @param element Root element from which we wanna get all used products from
 * @param productsLookup Record of all available products
 * @param recipes Include the list of recipes in store to make sure all products referred by recipes are included
 * @returns
 */
export const getProductRecordFromElement = (
  element: OneOfElements,
  productsLookup: ProductRecord,
  recipes?: Recipe[],
): Record<ProductID, IProduct> => {
  return getProductIdsInElement(element, recipes).reduce<
    Record<ProductID, IProduct>
  >(
    (acc, id) => {
      const product = productsLookup[id];
      return product
        ? {
            ...acc,
            [id]: product,
          }
        : acc;
    },
    {} as Record<ProductID, IProduct>,
  );
};

/**
 * Merge all version.product records into one record
 * @param project
 */
export const getProjectProductsRecord = (project: Project): ProductRecord =>
  getAllBuildingVersions(project).reduce(
    (acc, version) => ({
      ...acc,
      ...version.products,
    }),
    {},
  );

/**
 * Get all unique product ids that exists within an element (including children)
 * @param element
 * @param recipes Passing recipes will make sure all ProductIDs in referred recipes are included
 * @param productsLookup If provided we will remove any products that are not in the productsLookup
 * @returns
 */
export const getProductIdsInElement = (
  element: OneOfElements,
  recipes?: Recipe[],
  productsLookup?: ProductRecord,
): ProductID[] => {
  const usedRecipes = getRecipesUsedInElement(element, recipes);

  const recipesWithoutDeletedProducts = productsLookup
    ? usedRecipes.map((recipe) =>
        removeDeletedProductRefsInRecipe(productsLookup, recipe),
      )
    : usedRecipes;

  const recipeProductIds = getProductIdsInRecipes(
    ...recipesWithoutDeletedProducts,
  );

  // Include generic product (when mapping an EPD to the product) if present
  const productIds = Object.values(getProductIdsInElementMap(element)).flat();

  return uniq([...productIds, ...recipeProductIds]);
};

export const getProductIdsInElementMap = (
  element: OneOfElements,
): Record<IElementID, ProductID[]> => {
  // Include generic product (when mapping an EPD to the product) if present
  return getAllProductElements(element).reduce(
    (acc, productElement) => ({
      ...acc,
      [productElement.id]: getProductIdsInProductElement(productElement),
    }),
    {},
  );
};

/**
 * Get all product ids in a product element (generic product id as second id if it exists)
 * @param param0
 * @returns
 */
export const getProductIdsInProductElement = ({
  generic_product_id,
  product_id,
}: IProductElement): [ProductID] | [ProductID, ProductID] =>
  generic_product_id ? [product_id, generic_product_id] : [product_id];

/**
 * Get all product elements in an element (including children) in order of element hierarchy
 * @param element
 * @param products
 * @returns
 */
export const getProductsInElement = (
  element: OneOfElements,
  productLookup: ProductRecord,
): IProduct[] => {
  const productIds = getProductIdsInElement(element);
  return productIds.map((id) => productLookup[id]).filter(isDefined);
};

/**
 * Get all product ids from a product record or array of products
 * @param productRecordOrArray
 * @returns
 */
export const toProductIds = (
  productRecordOrArray?: ProductRecord | IProduct[] | ProductID[],
): ProductID[] =>
  Object.values(productRecordOrArray ?? []).map((a: string | IProduct) =>
    getId<IProduct>(a),
  );

/**
 * Product ids referenced by product elements on the subtree stored on a recipe-apply action
 * ({@link ElementRecipeApplyChange.resolvedElement} or stamped {@link Recipe.elements} children).
 * Does not scan the catalog recipe definition beyond that stamped subtree.
 */
export const getProductIdsFromElementRecipeApplyChange = (
  change: Pick<
    ProjectChangeMap[ProjectChangeType.ElementRecipeApply],
    'elementId' | 'recipe' | 'resolvedElement'
  >,
): ProductID[] => {
  const subtree: OneOfElements =
    change.resolvedElement ??
    ({
      kind: ElementKind.Element,
      id: change.elementId,
      elements: change.recipe.elements ?? [],
    } as OneOfElements);

  // No recipe catalog lookup — only `product_id` on nodes in the action subtree.
  return getProductIdsInElement(subtree, [], undefined);
};

/**
 * Product ids from {@link ProjectChangeType.ElementRecipeApply} actions that are not in
 * {@link Baseline.products}.
 */
export const getMissingActionRecipeApplyProductIdsInVersion = (
  version: Baseline,
): ProductID[] => {
  if (!version.actions?.length) return [];

  const referenced = uniq(
    version.actions.flatMap((action) =>
      action.changes.flatMap((change) =>
        change.type === ProjectChangeType.ElementRecipeApply
          ? getProductIdsFromElementRecipeApplyChange(change)
          : [],
      ),
    ),
  );

  return referenced.filter((id) => !version.products[id]);
};

/**
 * Collect product IDs referenced by a single proposal/version action change.
 * @param change
 */
export const getProductIdsFromProjectChange = (
  change: ProjectChange,
): ProductID[] => {
  switch (change.type) {
    case ProjectChangeType.ProductElementEpd:
      return [change.productId, change.genericProductId].filter(isDefined);
    case ProjectChangeType.ProductElementProductId:
      return [change.productId];
    case ProjectChangeType.ElementRecipeApply:
      return getProductIdsFromElementRecipeApplyChange(change);
    case ProjectChangeType.VersionProducts:
      return Object.keys(change.products);
    default:
      return [];
  }
};

/**
 * Collect all product IDs referenced by proposal {@link Action} changes on a version
 * (EPD swaps, product id changes, embedded recipes on {@link ProjectChangeType.ElementRecipeApply}, etc.).
 * Used so {@link Baseline.products} always contains every id actions may apply.
 * @param version
 */
export const getProductIdsFromVersionActions = (
  version: Baseline,
): ProductID[] => {
  if (!version.actions?.length) return [];
  return uniq(
    version.actions.flatMap((action) =>
      action.changes.flatMap((change) =>
        getProductIdsFromProjectChange(change),
      ),
    ),
  );
};

/**
 * Get any product ids that is missing from the version products record.
 * Also includes products referenced by proposal actions (e.g. {@link ProjectChangeType.ProductElementEpd})
 * so custom EPDs added via proposals are always present in the catalog after enrichment.
 *
 * Pass {@link proposalProductIds} (from {@link getProductIdsFromResolvedProposals}) to also
 * ensure proposal-resolved products are added. Compute this set once per pipeline step and
 * pass it down to avoid redundant O(N×P) proposal resolution.
 * @param version
 * @param recipes Passing recipes will make sure all ProductIDs in referred recipes are included
 * @param proposalProductIds IDs derived from resolved proposal trees that must be present
 */
export const getMissingProductIdsInVersion = (
  version: Baseline,
  productsLookup = genericProductsLookup,
  recipes: Recipe[] = [],
  proposalProductIds: ReadonlySet<ProductID> = new Set(),
): ProductID[] => {
  const mergedLookup = { ...productsLookup, ...version.products };

  const elementProductIds = getProductIdsInElement(
    version,
    recipes,
    mergedLookup,
  );
  // Include products referenced only through proposal actions (not in baseline elements).
  // Skip ids with no product payload anywhere — addMissingProductsToVersion would throw on required().
  const actionProductIds = getProductIdsFromVersionActions(version).filter(
    (id) => mergedLookup[id],
  );
  // Proposal-resolved products passed by caller (computed once per pipeline step)
  const proposalIds = [...proposalProductIds].filter((id) => mergedLookup[id]);

  return uniq([
    ...elementProductIds,
    ...actionProductIds,
    ...proposalIds,
  ]).filter((id) => !version.products[id]);
};

/**
 * Get any product ids that is obsolete in the version products record.
 * Products referenced by proposal actions ({@link ProjectChangeType.ProductElementEpd}) are
 * never considered obsolete — removing them would cause unknown product names when a proposal
 * is selected and the org store is not available.
 *
 * Pass {@link proposalProductIds} (from {@link getProductIdsFromResolvedProposals}) to also
 * protect proposal-resolved products from removal. Compute this set once per pipeline step
 * and pass it down to avoid redundant O(N×P) proposal resolution.
 * @param version
 * @param recipes Passing recipes will make sure all ProductIDs in referred recipes are included
 * @param proposalProductIds IDs derived from resolved proposal trees that must not be removed
 */
export const getObsoleteProductIdsInVersion = (
  version: Baseline,
  recipes: Recipe[] = [],
  proposalProductIds: ReadonlySet<ProductID> = new Set(),
): ProductID[] => {
  const elementProductIds = getProductIdsInElement(version, recipes);
  // Action-referenced and proposal-resolved products must not be removed — they may only
  // appear on enriched proposal trees, not on the baseline element tree.
  const actionProductIds = getProductIdsFromVersionActions(version);
  const allReferencedIds = new Set([
    ...elementProductIds,
    ...actionProductIds,
    ...proposalProductIds,
  ]);
  return Object.keys(version.products).filter(
    (id) => !allReferencedIds.has(id),
  );
};

/**
 * Get all products in productRecord that are not in missingRecord
 * @param productRecord Record of products to check
 * @param missingRecord Record to make sure they exist in
 */
export const getMissingProducts = (
  productRecord: ProductRecord,
  missingRecord: ProductRecord,
): IProduct[] =>
  Object.values(productRecord).filter((product) => !missingRecord[product.id]);

/**
 * Update a version with the latest products.
 * Will remove any obsolete products and add any missing products.
 *
 * Pass {@link proposalProductIds} (from {@link getProductIdsFromResolvedProposals}) to add
 * proposal-resolved products and protect them from obsolete removal in a single pass.
 * @param version
 * @param productsLookup
 * @param recipes Include the list of recipes in store to make sure all products referred by recipes are included
 * @param proposalProductIds IDs derived from resolved proposal trees (compute once per pipeline step)
 */
export const updateVersionProductRecord = (
  version: Baseline,
  productsLookup: ProductRecord,
  recipes?: Recipe[],
  proposalProductIds: ReadonlySet<ProductID> = new Set(),
): Baseline => {
  version = addMissingProductsToVersion(
    version,
    productsLookup,
    recipes,
    proposalProductIds,
  );
  version = removeObsoleteProductsInVersion(
    version,
    productsLookup,
    recipes,
    proposalProductIds,
  );

  // Return the version with updated products or original if no changes
  return version;
};

/**
 * Add all missing products to version.products.
 * Will keep any obsolete products.
 *
 * Pass {@link proposalProductIds} (from {@link getProductIdsFromResolvedProposals}) to also
 * include proposal-resolved products that are not referenced in the baseline element tree.
 * @param version
 * @param productsLookup
 * @param recipes Include the list of recipes in store to make sure all products referred by recipes are included
 * @param proposalProductIds IDs derived from resolved proposal trees (compute once per pipeline step)
 */
export const addMissingProductsToVersion = (
  version: Baseline,
  productsLookup: ProductRecord,
  recipes?: Recipe[],
  proposalProductIds: ReadonlySet<ProductID> = new Set(),
): Baseline => {
  // Make sure we have all products from the version and generic products
  productsLookup = {
    ...genericProductsLookup,
    ...productsLookup,
    ...version.products,
  };

  const missingIds = getMissingProductIdsInVersion(
    version,
    productsLookup,
    recipes,
    proposalProductIds,
  );

  if (missingIds.length === 0) {
    return version;
  }

  // Map to generic ids from the productsLookup (not in version.products)
  const idToGenericMap = keyArrayToRecord(
    missingIds,
    (id) => productsLookup[id]?.generic_id,
  );
  const productIdMap = getProductIdsInElementMap(version);

  const productIdMapValues = Object.values(productIdMap); // Array<[ElementID, [ProductID, GenericProductID]]>
  // const missingProducts = cloneDeep(pick(productsLookup, ...missingIds))

  const missingProducts = missingIds.reduce((acc, id) => {
    const product = required(
      cloneDeep(productsLookup[id]),
      MISSING_PRODUCT_ERROR,
    );

    // Second item in array is the generic product id if it exists (prio generic id from the EPD from store)
    const generic_id =
      idToGenericMap[id] ?? // Prefer using conversion factors from the EPDs generic product to get the same values in every usage
      productIdMapValues.find(([prodId]) => id === prodId)?.[1];

    // Use the conversion factors from the generic product if they are missing in the EPD
    if (generic_id) {
      const generic = required(
        version.products[generic_id] ?? productsLookup[generic_id],
        MISSING_PRODUCT_ERROR,
      );

      product.conversion_factors = mergeConversionFactors(
        generic.conversion_factors,
        product.conversion_factors,
      );
    }

    return {
      ...acc,
      [id]: product,
    };
  }, {} as ProductRecord);

  if (missingIds.length > 0) {
    version = {
      ...version,
      products: {
        // Pick missing products and clone them to avoid mutating the original
        ...missingProducts,
        ...version.products,
      },
    };
  }

  // Return the version with updated products or original if no changes
  return version;
};

/**
 * Remove any obsolete products from version.products.
 *
 * Pass {@link proposalProductIds} (from {@link getProductIdsFromResolvedProposals}) to
 * protect proposal-resolved products from being removed.
 * @param version
 * @param productsLookup
 * @param recipes Include the list of recipes in store to make sure all products referred by recipes are included
 * @param proposalProductIds IDs derived from resolved proposal trees that must not be removed
 */
export const removeObsoleteProductsInVersion = (
  version: Baseline,
  productsLookup: ProductRecord,
  recipes?: Recipe[],
  proposalProductIds: ReadonlySet<ProductID> = new Set(),
): Baseline => {
  const obsoleteIds = getObsoleteProductIdsInVersion(
    version,
    recipes,
    proposalProductIds,
  );

  if (obsoleteIds.length > 0) {
    version = {
      ...version,
      products: omit(version.products, ...obsoleteIds),
    };
  }

  // Return the version with updated products or original if no changes
  return version;
};

/**
 * Update all version.products with the latest products.
 * Will remove any obsolete products and add any missing products.
 * @param project
 * @param productRecord
 * @param recipe
 * @returns
 */
export const updateProductsInProject = (
  project: Project,
  productRecord: ProductRecord,
  recipes: Recipe[],
): Project => {
  const building = getBuilding(project);

  // No products to update, return original project
  if (!Object.keys(productRecord).length) {
    return project;
  }

  // Update the product record in the project baselines
  const baselines = building.baselines.map((version) =>
    updateVersionProductRecord(version, productRecord, recipes),
  );

  // Nothing have changed, return original project
  if (building.baselines.every((current, i) => current === baselines[i])) {
    return project;
  }

  return {
    ...project,
    baselines,
  };
};

/**
 * Updates all products in a project with the latest in productsLookup.
 * Will remove any unused products. Will also keep products that are removed from DB but still used in project.
 * DO NOT USE THIS TO PRODUCTS without a specific action from the user since it will overwrite any products.
 * Default behaviour should use updateVersionProductRecord instead.
 * @param project
 * @param productsLookup
 * @returns
 */
export const forceUpdateProductsInProject = (
  project: Project,
  productsLookup: ProductRecord,
  resetCost?: boolean,
): Project => {
  const versions = getAllBuildingVersions(project);

  const changes: Array<SemiPartial<Baseline, 'id'>> = versions.reduce(
    (acc, version) => {
      const { id } = version;

      // All currently used products
      const currentProducts = getProductRecordFromElement(
        version,
        version.products,
      );

      // Latest products from DB (with cost from the versions if available)
      const latestProducts = mapValues(
        getProductRecordFromElement(version, productsLookup),
        (p) => {
          const currentFactors: ConversionFactors =
            currentProducts[p.id]?.conversion_factors ?? {};
          // Keep cost from version products if set since they are editable
          const cost = resetCost
            ? p.conversion_factors['sek_A1-A3']
            : currentFactors['sek_A1-A3'] || p.conversion_factors['sek_A1-A3'];
          const conversion_factors = omitUndefined({
            ...p.conversion_factors,
            'sek_A1-A3': cost,
          });
          return { ...p, conversion_factors };
        },
      );

      const products = {
        // Keep old products removed from the db (or if productsLookup have not loaded)
        ...currentProducts,
        // Overwrite with any new products founds
        ...latestProducts,
      };

      return [...acc, { id, products }];
    },
    [] as Array<SemiPartial<Baseline, 'id'>>,
  );

  // Only modify the project if there are changes (to avoid unnecessary re-renders)
  const updatedProductsProject = updateBuildingVersionProperties(
    project,
    ...changes,
  );

  return updatedProductsProject;
};

export const isProduct = (product: unknown): product is IProduct => {
  return (
    isObject(product) &&
    hasDefinedProperties(
      product as IProduct,
      'id',
      'conversion_factors',
      'unit',
    )
  );
};

export const isCustomProductId = (
  productOrId: ItemOrItemId<IProduct>,
): boolean => getId(productOrId).startsWith(CUSTOM_ID_PREFIX);

export const isBoverketProductId = (
  productOrId?: ItemOrItemId<IProduct>,
): boolean =>
  !!productOrId && getId(productOrId).startsWith(BOVERKET_ID_PREFIX);

export const isNodonProductId = (
  productOrId: ItemOrItemId<IProduct>,
): boolean => getId(productOrId).startsWith(NODON_ID_PREFIX);

export const isOekobaudatProductId = (
  productOrId: ItemOrItemId<IProduct>,
): boolean => getId(productOrId).startsWith(OKOBAUDAT_ID_PREFIX);

export const isCo2DataFiProductId = (
  productOrId?: ItemOrItemId<IProduct>,
): boolean =>
  !!productOrId && getId(productOrId).startsWith(CO2DATA_FI_ID_PREFIX);

/**
 * Get id prefix from product source like boverket_sv-SE_
 * @param source
 * @returns
 */
export const getProductIdPrefixFromSource = (
  source: IProduct['source'],
): string => {
  switch (source) {
    case ProductSources.Co2DataFi:
      return CO2DATA_FI_ID_PREFIX;
    case ProductSources.Boverket:
      return BOVERKET_ID_PREFIX;
    case ProductSources.Nodon:
      return NODON_ID_PREFIX;
    case ProductSources.Ökobaudat:
      return OKOBAUDAT_ID_PREFIX;
    case ProductSources.Custom:
      return CUSTOM_ID_PREFIX;
  }
  throw new Error(`Unknown product source: ${String(source)}`);
};
/**
 * Get the original product ID. The ID before it was prefixed boverket_sv-SE_ or suffixed with climate improved suffixes
 * @param product
 * @returns
 */
export const getOriginalProductId = (
  productOrId: ItemOrItemId<IProduct>,
): string => {
  return getId(productOrId)
    .replace(BOVERKET_ID_PREFIX, '')
    .replace(OKOBAUDAT_ID_PREFIX, '')
    .replace(NODON_ID_PREFIX, '')
    .replace(CO2DATA_FI_ID_PREFIX, '')
    .replace('sv-SE_', '')
    .replace(/-\d+$/g, ''); // Remove climate improved suffixes
};

export const getProductCategories = (
  product: IProduct,
  key: keyof ProductCategories,
): string[] => {
  if (key === 'Custom') {
    return ['Custom'];
  }
  const main = product.categories[key] ?? {};
  return typeof main === 'boolean'
    ? [key]
    : Object.keys(main).filter((k) => main[k]);
};

/**
 * Replace all product ids in a project with a new id
 * @param project
 * @param replaceRecord A record looking like: <Id to replace, New product>
 * @returns
 */
export const replaceProductIds = (
  project: Project,
  replaceRecord: ProductRecord,
): Project => {
  if (Object.keys(replaceRecord).length === 0) {
    return project;
  }
  const versions = getAllBuildingVersions(project);
  const productElements = getAllProductElements(...versions).filter(
    (e) => !!replaceRecord[e.product_id],
  );

  const changes = productElements.map((e) => {
    const product = replaceRecord[e.product_id];

    return {
      id: e.id,
      product_id: product?.id,
    };
  });
  return updateElements(project, ...changes);
};

export const parseProduct = (raw: ProductJSON): IProduct => {
  const created_at = DateTime.fromISO(raw.created_at).toString();
  const updated_at = DateTime.fromISO(raw.updated_at).toString();
  const deleted_at = raw.deleted_at
    ? DateTime.fromISO(raw.deleted_at).toString()
    : undefined;

  return {
    ...raw,
    created_at,
    updated_at,
    deleted_at,
    description: raw.description ?? undefined,
    owner: raw.owner ?? undefined,
    organizations: raw.organizations ?? undefined,
  };
};

export const productIDregex = /^(boverket_sv-SE_\d{10}|-)$/;
export const productNodonIDregex = /^(nodon_sv-SE_\d{10}|-)$/;

/**
 * Check if two elements contain the same products
 * @param elementA
 * @param elementB
 * @returns
 */
export const hasEqualProductsIds = (
  elementA: OneOfElements,
  elementB: OneOfElements,
): boolean => {
  if (elementA.kind !== elementB.kind) {
    throw new Error('Cannot compare different kinds of elements');
  }
  const productIdsA = getChildElements(elementA)
    .filter(isProductElement)
    .map((child) => child.product_id);
  const productIdsB = getChildElements(elementB)
    .filter(isProductElement)
    .map((child) => child.product_id);
  return shallowEqual(productIdsA, productIdsB);
};

export const convertToProductsRecord = (products: IProduct[]): ProductRecord =>
  products.reduce<ProductRecord>(
    (acc, product) => ({
      ...acc,
      [product.id]: product,
    }),
    {},
  );

/**
 * Add a product id to a list of generic ids if it's not already included
 * @param ids
 * @param id
 * @returns
 */
export const addIdToGenericIds = (
  ids: ProductID[] | undefined,
  id: ProductID,
): ProductID[] => {
  if (!ids) {
    return [id];
  }
  return ids.includes(id) ? ids : [...ids, id];
};

export const removeIdFromGenericIds = (
  ids: ProductID[],
  id: ProductID,
): ProductID[] => ids.filter((i) => i !== id);

export const getProductSourceFromId = (
  id?: ProductID,
): IProduct['source'] | undefined => {
  if (!id) {
    return;
  }
  if (isBoverketProductId(id)) {
    return ProductSources.Boverket;
  }
  if (isNodonProductId(id)) {
    return ProductSources.Nodon;
  }
  if (isOekobaudatProductId(id)) {
    return ProductSources.Ökobaudat;
  }
  if (isCustomProductId(id)) {
    return ProductSources.Custom;
  }
  if (isCo2DataFiProductId(id)) {
    return ProductSources.Co2DataFi;
  }
};

export const getAverageConversionFactors = (
  ...factors: ConversionFactors[]
): ConversionFactors => {
  if (factors.length === 0) {
    return {};
  }
  if (factors.length === 1) {
    return factors[0]!;
  }

  return getMean(...factors);
};

export const isOneOfProductSources = (
  str: string | ProductSources | ProductSourcesUnion,
): str is ProductSourcesUnion => {
  return isOneOf(Object.values(ProductSources), str);
};

/**
 * Check if a string matches a product source
 * @param str
 * @param source
 * @returns
 */
export const isProductSource = <T extends ProductSourcesUnion>(
  str: string | ProductSources | T,
  source: T,
): str is T => {
  return str.toLowerCase() === source.toLowerCase();
};
